import { useState, useEffect } from 'react';
import { geocodeAddress } from '../utils/geocodeWithMaps';
import { searchPlacesTextPreferred } from '../utils/placesTextSearch';
import { haversineMi } from '../utils/haversine';
import { parseCityState, GROCERY_PLACE_TYPES, RESTAURANT_LIKE_TYPES, isGroceryPlace } from '../utils/ffParser';
import { FAVORITE_MODE_AUTO, FAVORITE_MODE_BRAND, FAVORITE_MODE_BROAD, inferFavoriteMode } from '../utils/favorites';
import { detectSearchScope } from '../utils/searchScopes';

const MIN_REVIEWS = 20;
const MIN_STARS = 3.1;
const MAX_DISTANCE_MI = 15;

function normalizeTerm(value) {
  return String(value || '').trim().toLowerCase();
}

function isCategoryLike(term) {
  return term.split(/\s+/).length <= 2;
}

async function geocodeOneWithResult(addr) {
  const data = await geocodeAddress(addr);
  if (data.status !== 'OK' || !data.results?.[0]) return null;
  const result = data.results[0];
  const loc = result.geometry.location;
  return { coord: [loc.lat, loc.lng], result };
}

/** Search grocery stores near a center, apply type/rating filters and recency check. */
function normalizeFavoritesForSearch(favorites, favoriteStoreNames) {
  if (Array.isArray(favorites) && favorites.length > 0) return favorites;
  return (favoriteStoreNames || []).map((name) => ({ label: name, query: name, mode: FAVORITE_MODE_AUTO }));
}

async function searchGroceries(centerLat, centerLng, favorites, favoriteStoreNames, cancelled) {
  const searchData = await searchPlacesTextPreferred({
    textQuery: 'grocery store',
    lat: centerLat,
    lng: centerLng,
    pageSize: 20,
    includeRating: true,
    includeTypes: true,
    includedType: 'grocery_store',
    useStrictTypeFiltering: true,
  });
  if (cancelled()) return [];

  const places = searchData.places || [];
  const favoriteItems = normalizeFavoritesForSearch(favorites, favoriteStoreNames);

  let allStores = places
    .map((p, i) => {
      const loc = p.location;
      const lat = loc?.latitude;
      const lng = loc?.longitude;
      const name = p.displayName?.text || `Grocery store ${i + 1}`;
      const distMi = lat != null && lng != null ? haversineMi(lat, lng, centerLat, centerLng) : null;
      const normalizedName = normalizeTerm(name);
      const isFavorite = favoriteItems.some((fav) => {
        const favQuery = normalizeTerm(fav.query || fav.label);
        if (!favQuery) return false;
        const resolvedMode = fav.mode || inferFavoriteMode(fav.label);
        if (resolvedMode === FAVORITE_MODE_BRAND) return normalizedName.includes(favQuery);
        if (resolvedMode === FAVORITE_MODE_BROAD) return false;
        return normalizedName.includes(favQuery);
      });
      return {
        id: p.id || `grocery-${i}`,
        lat, lng, name,
        label: String(i + 1),
        distanceMi: distMi,
        isFavorite: !!isFavorite,
        isGrocery: true,
        rating: p.rating != null ? Number(p.rating) : null,
        userRatingCount: p.userRatingCount != null ? Number(p.userRatingCount) : null,
        primaryType: p.primaryType,
        types: p.types,
      };
    })
    .filter((s) => {
      if (s.lat == null || s.lng == null || s.distanceMi == null || s.distanceMi > MAX_DISTANCE_MI || (s.userRatingCount ?? 0) < MIN_REVIEWS || (s.rating ?? 0) < MIN_STARS)
        return false;
      const primary = s.primaryType ? String(s.primaryType) : null;
      if (!primary) return false;
      if (GROCERY_PLACE_TYPES.has(primary)) return true;
      if (primary === 'establishment') {
        const types = s.types || [];
        return types.some((t) => GROCERY_PLACE_TYPES.has(String(t))) && !types.some((t) => RESTAURANT_LIKE_TYPES.has(String(t)));
      }
      return false;
    });

  allStores.sort((a, b) => a.distanceMi - b.distanceMi);
  const stores = allStores.slice(0, 5);
  stores.forEach((store, i) => { if (!store.isFavorite) store.label = String(i + 1); });

  // Find unmatched favorites and search for them in parallel
  const matchedFavKeys = new Set();
  stores.forEach((s) => {
    if (s.isFavorite && s.name) {
      favoriteItems.forEach((fav) => {
        const favQuery = normalizeTerm(fav.query || fav.label);
        if (!favQuery) return;
        const resolvedMode = fav.mode || inferFavoriteMode(fav.label);
        if (resolvedMode === FAVORITE_MODE_BRAND && s.name.toLowerCase().includes(favQuery)) {
          matchedFavKeys.add(`${resolvedMode}:${favQuery}`);
        }
      });
    }
  });
  const unmatchedFavs = favoriteItems.filter((fav) => {
    const favQuery = normalizeTerm(fav.query || fav.label);
    const resolvedMode = fav.mode || inferFavoriteMode(fav.label);
    if (!favQuery) return false;
    return !matchedFavKeys.has(`${resolvedMode}:${favQuery}`);
  });

  // Search unmatched favorites in parallel and support both specific brands and broad categories.
  const MAX_PLACES_PER_FAVORITE = 8;
  const favResultArrays = await Promise.all(
    unmatchedFavs.map(async (favorite) => {
      if (cancelled()) return [];
      const searchName = String(favorite.query || favorite.label || '').trim();
      if (!searchName) return [];

      const want = normalizeTerm(searchName);
      const tokens = want.split(/\s+/).filter(Boolean);
      const resolvedMode = favorite.mode || inferFavoriteMode(favorite.label);
      const categoryMode = resolvedMode === FAVORITE_MODE_BROAD || isCategoryLike(want);

      const matchByNameOrType = (p) => {
        const placeName = normalizeTerm(p.displayName?.text || '');
        const primaryType = normalizeTerm(p.primaryType || '');
        const typeText = Array.isArray(p.types) ? p.types.map((t) => normalizeTerm(t)).join(' ') : '';
        const haystack = `${placeName} ${primaryType} ${typeText}`;
        if (!haystack) return false;
        if (resolvedMode === FAVORITE_MODE_BRAND) return placeName.includes(want);
        if (haystack.includes(want)) return true;
        return tokens.every((w) => haystack.includes(w));
      };

      try {
        const data = await searchPlacesTextPreferred({
          textQuery: categoryMode ? `${searchName} near me` : searchName,
          lat: centerLat,
          lng: centerLng,
          pageSize: MAX_PLACES_PER_FAVORITE,
          includeTypes: true,
          includeRating: true,
        });
        const placesList = data.places || [];
        const withCoords = placesList.filter((p) => p?.location?.latitude != null && p?.location?.longitude != null);
        const matching = withCoords.filter(matchByNameOrType);
        const toAdd = (matching.length > 0 ? matching : withCoords).slice(0, MAX_PLACES_PER_FAVORITE);

        return toAdd
          .filter((p) => (p.rating ?? 0) >= 3.0)
          .map((place, idx) => {
            const lat = place.location.latitude;
            const lng = place.location.longitude;
            return {
              id: place.id || `fav-${searchName}-${idx}-${lat}-${lng}`,
              lat, lng,
              name: place.displayName?.text || searchName,
              label: '★',
              distanceMi: haversineMi(lat, lng, centerLat, centerLng),
              isFavorite: true,
              favoriteMode: resolvedMode,
              favoriteLabel: favorite.label,
              isGrocery: isGroceryPlace(place),
              rating: place.rating != null ? Number(place.rating) : null,
              userRatingCount: place.userRatingCount != null ? Number(place.userRatingCount) : null,
            };
          })
          .filter((p) => p.distanceMi <= MAX_DISTANCE_MI);
      } catch (_) { return []; }
    })
  );
  if (cancelled()) return [];

  const extraStores = favResultArrays.flat();

  const combined = [...stores, ...extraStores];
  combined.sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity));
  return combined;
}

/** Search gyms near a center with rating/recency filters. */
async function searchGyms(centerLat, centerLng, cancelled) {
  const gymData = await searchPlacesTextPreferred({
    textQuery: 'gym',
    lat: centerLat,
    lng: centerLng,
    pageSize: 20,
    includeRating: true,
    includeTypes: true,
    includedType: 'gym',
    useStrictTypeFiltering: false,
  });
  if (cancelled()) return [];

  let gymCandidates = (gymData.places || [])
    .map((p, i) => {
      const loc = p.location;
      const lat = loc?.latitude;
      const lng = loc?.longitude;
      return {
        id: p.id || `gym-${i}`,
        lat, lng,
        name: p.displayName?.text || `Gym ${i + 1}`,
        label: String(i + 1),
        distanceMi: lat != null && lng != null ? haversineMi(lat, lng, centerLat, centerLng) : null,
        isFavorite: false,
        rating: p.rating != null ? Number(p.rating) : null,
        userRatingCount: p.userRatingCount != null ? Number(p.userRatingCount) : null,
        primaryType: p.primaryType,
        types: p.types,
      };
    })
    .filter((s) => s.lat != null && s.lng != null && s.distanceMi != null && s.distanceMi <= MAX_DISTANCE_MI && (s.userRatingCount ?? 0) >= MIN_REVIEWS && (s.rating ?? 0) >= MIN_STARS);

  gymCandidates.sort((a, b) => a.distanceMi - b.distanceMi);
  let gymList = gymCandidates;
  gymList = gymList.slice(0, 10);
  gymList.forEach((g, i) => { g.label = String(i + 1); });
  return gymList;
}

/** Scrape FF listings for a work address. */
async function scrapeFF(workCoord, workResult, cancelled) {
  const { state, ffCity } = parseCityState(workResult.address_components || []);
  if (!state || !ffCity) return { html: null, error: null };
  const [workLat, workLng] = workCoord;
  try {
    const scrapeRes = await fetch(
      `/api/ffscrape?${new URLSearchParams({ state, city: ffCity, lat: workLat, lng: workLng })}`
    );
    if (cancelled()) return { html: null, error: null };
    const data = await scrapeRes.json().catch(() => ({}));
    if (scrapeRes.ok && data.html != null) {
      return { html: data.html, error: null };
    }
    return { html: null, error: data.error || `HTTP ${scrapeRes.status}` };
  } catch (e) {
    if (cancelled()) return { html: null, error: null };
    return { html: null, error: e.message || 'Request failed' };
  }
}

/**
 * Main search orchestration hook. Geocodes addresses, then prioritizes
 * map-critical data (grocery + gyms). FF scrape runs in background so map
 * interactivity is not blocked by slow anti-bot scraping.
 */
export function useSearchFlow({
  workingAddress,
  stayingAddress,
  favorites,
  favoriteStoreNames,
  hideFurnishedFinder,
  navigate,
  onClassifyFavorite,
}) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [workCenter, setWorkCenter] = useState(null);
  const [homeCenter, setHomeCenter] = useState(null);
  const [groceryStores, setGroceryStores] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [ffHtml, setFfHtml] = useState(null);
  const [ffScrapeError, setFfScrapeError] = useState(null);
  const [searchedState, setSearchedState] = useState('');
  const [searchScope, setSearchScope] = useState({ mode: 'address', state: '', city: '', query: '' });

  useEffect(() => {
    const hasWork = !!workingAddress;
    const hasStay = !!stayingAddress;
    if (!hasWork && !hasStay) {
      navigate('/', { replace: true });
      return;
    }

    setStatus('loading');
    setError(null);
    setWorkCenter(null);
    setHomeCenter(null);
    setGroceryStores([]);
    setGyms([]);
    setFfHtml(null);
    setFfScrapeError(null);
    setSearchedState('');
    setSearchScope(detectSearchScope(workingAddress));

    let cancelled = false;
    const isCancelled = () => cancelled;

    async function run() {
      try {
        const detectedScope = detectSearchScope(workingAddress);
        setSearchScope(detectedScope);

        // 1. Geocode both addresses in parallel
        const [workData, stayData] = await Promise.all([
          hasWork ? geocodeOneWithResult(workingAddress) : null,
          hasStay ? geocodeOneWithResult(stayingAddress) : null,
        ]);
        if (cancelled) return;

        const workCoord = workData?.coord ?? null;
        const stayCoord = stayData?.coord ?? null;
        if (hasWork && !workCoord) { setError('Could not find work address.'); setStatus('error'); return; }
        if (hasStay && !stayCoord) { setError('Could not find home address.'); setStatus('error'); return; }
        setWorkCenter(workCoord);
        setHomeCenter(stayCoord);
        if (workData?.result?.address_components) {
          const { state } = parseCityState(workData.result.address_components);
          setSearchedState(state || '');
        }

        // 2. Load map-critical layers first (grocery + gyms)
        const placesCenter = detectedScope.mode === 'state' ? null : (stayCoord ?? workCoord);
        const [centerLat, centerLng] = placesCenter || [];

        const groceryPromise = placesCenter
          ? searchGroceries(centerLat, centerLng, favorites, favoriteStoreNames, isCancelled)
          : Promise.resolve([]);

        const gymPromise = placesCenter
          ? searchGyms(centerLat, centerLng, isCancelled)
          : Promise.resolve([]);

        const [groceryResult, gymResult] = await Promise.all([
          groceryPromise.catch(() => []),
          gymPromise.catch(() => []),
        ]);
        if (cancelled) return;

        setGroceryStores(groceryResult);
        setGyms(gymResult);
        setStatus('success');

        if (typeof onClassifyFavorite === 'function') {
          (favorites || []).forEach((favorite) => {
            if (!favorite?.label || favorite.mode) return;
            onClassifyFavorite(favorite);
          });
        }

        // 3. FF scrape in background (non-blocking for map usability)
        if (!hideFurnishedFinder && detectedScope.mode !== 'state' && hasWork && workData?.result && workCoord) {
          scrapeFF(workCoord, workData.result, isCancelled)
            .then((ffResult) => {
              if (cancelled) return;
              setFfHtml(ffResult.html);
              setFfScrapeError(ffResult.error);
            })
            .catch(() => {
              if (cancelled) return;
              setFfHtml(null);
              setFfScrapeError('Request failed');
            });
        }
      } catch (err) {
        if (!cancelled) {
          setError('Request failed. Check your connection and try again.');
          setStatus('error');
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [workingAddress, stayingAddress, navigate, favorites, favoriteStoreNames, hideFurnishedFinder, onClassifyFavorite]);

  return { status, error, workCenter, homeCenter, groceryStores, gyms, ffHtml, ffScrapeError, searchedState, searchScope };
}
